/**
 * SHARP Inference Plugin
 *
 * Image -> Gaussian Splat (.spz) via the SHARP API.
 * Special: also supports prompt -> generated image -> splat (Flow B, AI-only).
 */

import type { SceneObjectData } from 'trace-viewer';
import { Utility } from 'trace-viewer';
import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import type { ToolContext } from '../ai/agent-helpers';
import { declareInferencePlugin } from '../plugins/inference-adapter';
import { applyTransform, resolveObject } from '../ai/agent-helpers';
import { generateImageBytes } from '../ai/tools/generation-tools';
import { INFERENCE_API_URL, getImageBlob, createObjectFromInference, inferencePost, isImageSceneObject, isVideoSceneObject, type InferenceStatusCallback } from './shared';
import { CreateObjectCommand, getUndoManager, topLevelParent } from 'trace-viewer/editor';

// ── Internal: run SHARP on a blob ──────────────────────────

export type SharpQuality = 'low' | 'medium' | 'high';

/**
 * Run SHARP inference on an image blob or SceneObject.
 * Internal helper used by both `run()` (SceneObject path) and
 * `executeToolOverride()` (Blob path for prompt-to-splat flow).
 */
async function runSplatInternal(
    source: SceneObject | Blob,
    traceScene: TraceScene,
    baseName?: string,
    onStatus?: InferenceStatusCallback,
    quality: SharpQuality = 'medium',
): Promise<SceneObject> {
    let imageBlob: Blob;
    let sourceObject: SceneObject | undefined;
    let resolvedBaseName: string;

    if (source instanceof Blob) {
        imageBlob = source;
        resolvedBaseName = baseName ?? 'GeneratedSplat';
    } else {
        sourceObject = source;
        imageBlob = await getImageBlob(source, traceScene);
        resolvedBaseName = source.name;
    }

    const imgExt = imageBlob.type === 'image/png' ? 'png' : 'jpg';

    const response = await inferencePost('sharp', () => {
        const formData = new FormData();
        formData.append('image', imageBlob, `image.${imgExt}`);
        formData.append('format', 'spz');
        formData.append('compression', 'zstd');
        formData.append('quality', quality);
        return [`${INFERENCE_API_URL}/sharp/predict`, { method: 'POST', body: formData }];
    }, undefined, onStatus);
    if (!response.ok) {
        const errText = await response.text();
        throw new Error(`SHARP API error (${response.status}): ${errText}`);
    }

    const splatBlob = await response.blob();
    const assetPath = await traceScene.resources.addAssetWithDedup(
        `assets/${resolvedBaseName}.spz`,
        splatBlob,
    );

    if (sourceObject) {
        return createObjectFromInference(traceScene, sourceObject, assetPath, 'splat');
    }

    // Blob-only flow: create object at scene root
    const allObjects = traceScene.getSceneObjects();
    const siblingNames = new Set(allObjects.map(o => o.name));
    const splatName = Utility.generateUniqueName(
        `${resolvedBaseName}_splat`,
        siblingNames,
    );

    const splatData: SceneObjectData = {
        name: splatName,
        src: `./${assetPath}`,
        transform: {},
        behaviors: [],
    };

    // Top-level content parents under the DOCUMENT ROOT (SCENE_ROOT_SPEC D8).
    const parent = topLevelParent(traceScene);
    const splatObject = await traceScene.createSceneObjectFromData(splatData, parent ?? undefined);

    if (!getUndoManager().isUndoRedoing) {
        getUndoManager().push(
            new CreateObjectCommand(splatObject, parent, splatData),
        );
    }

    traceScene.select(splatObject);
    return splatObject;
}

// ── AI Tool Override (Flow A + B) ──────────────────────────

async function executeGenerateSplat(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const objectRef = input.object as string | undefined;
    const prompt = input.prompt as string | undefined;
    const name = input.name as string | undefined;
    const position = input.position as [number, number, number] | undefined;
    const rotation = input.rotation as [number, number, number] | undefined;
    const scale = input.scale as [number, number, number] | undefined;
    const quality = (input.quality as SharpQuality | undefined) ?? 'medium';
    const { traceScene } = ctx;

    if (!objectRef && !prompt) {
        return 'Error: Either "object" (existing image to convert) or "prompt" (text to generate an image first) is required.';
    }

    try {
        let splatObject;

        if (objectRef) {
            // Flow A: Convert existing image or video (current frame)
            const target = resolveObject(objectRef, ctx);
            if (!target) return `Error: Object "${objectRef}" not found.`;
            if (!isImageSceneObject(target) && !isVideoSceneObject(target)) {
                return `Error: "${objectRef}" is not an image or video object. Only objects with .jpg/.jpeg/.png sources, or Video objects, can be converted to splats.`;
            }
            splatObject = await runSplatInternal(target, traceScene, undefined, undefined, quality);
        } else {
            // Flow B: Generate image from prompt first, then convert to splat
            console.log(`[AI] generate_splat: generating image for "${prompt!.slice(0, 100)}"`);
            const generated = await generateImageBytes(ctx, prompt!);
            if ('error' in generated) return `Error generating image for splat: ${generated.error}`;
            const imageBlob = generated.blob;
            const mimeType = imageBlob.type;

            // Store image in resources (not added to scene)
            const ext = generated.extension;
            const sanitized = prompt!.slice(0, 40).replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_');
            await traceScene.resources.addAssetWithDedup(`assets/generated_${sanitized}.${ext}`, imageBlob);

            const baseName = prompt!.slice(0, 30).replace(/[^a-zA-Z0-9 ]/g, '').trim() || 'GeneratedSplat';
            console.log(`[AI] generate_splat: image generated (${mimeType}, ${imageBlob.size} bytes), sending to SHARP`);

            splatObject = await runSplatInternal(imageBlob, traceScene, baseName, undefined, quality);
        }

        // Apply explicit transform overrides
        applyTransform(splatObject.transform, position, rotation, scale);

        if (name) {
            traceScene.renameSceneObject(splatObject, name);
        }

        ctx.onSceneChanged?.();

        const source = objectRef ? `image "${objectRef}"` : 'generated image';
        return `Created splat "${splatObject.name}" from ${source}.`;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return `Error generating splat: ${msg}`;
    }
}

// ── Plugin ─────────────────────────────────────────────────

declareInferencePlugin({
    id: 'sharp',
    sourceType: 'image',
    menuLabel: 'Make Splat (SHARP)',
    menuOrder: 200,

    toolName: 'generate_splat',
    extraInputProperties: {
        prompt: { type: 'string' },
        quality: {
            type: 'string',
            enum: ['low', 'medium', 'high'],
        },
    },
    objectOptional: true,
    toolModelLabel: 'SHARP',

    run: (sourceObject, traceScene, onStatus) => runSplatInternal(sourceObject, traceScene, undefined, onStatus),
    executeToolOverride: executeGenerateSplat,

    slashCommand: {
        name: 'splat',
        description: 'Convert image to Gaussian Splat (SHARP)',
        usage: '[object] or [prompt]',
        category: 'inference',
        parseArgs: (args, selected) => {
            if (!args && selected) return { object: selected };
            if (!args) return { object: 'selected' };
            return args.startsWith('"') || args.startsWith("'") || /^[\w/.-]+$/.test(args)
                ? { object: args }
                : { prompt: args };
        },
    },
});
